import { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';
import React, { FC, memo, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ContractUnitType } from 'entities/Contract';
import { generateInvoicePdfLink, Invoice } from 'entities/Invoice';
import { Modal } from 'shared/ui/Modal';
import { ServerTable } from 'shared/ui/ServerTable';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useGetUserInvoicesQuery } from 'entities/User';
import { ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ReactComponent as DownloadIcon } from 'shared/assets/icons/newIcons/DownloadIcon.svg';
import { ReactComponent as SendEmailIcon } from 'shared/assets/icons/newIcons/SendEmailIcon.svg';
import { useSendInvoiceByEmail } from 'features/SendInvoiceByEmail';
import { ReactComponent as PaymentIcon } from 'shared/assets/icons/newIcons/PaymentIcon.svg';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface InvoicesModalProps {
  isOpened: boolean;
  userId: string | undefined;
  onCLose: () => void;
  openInvoiceModal: (invoice: Invoice) => void;
  openPaymentModal: (invoice: Invoice) => void;
}

export const InvoicesModal: FC<InvoicesModalProps> = memo((props) => {
  const { isOpened, userId, onCLose, openInvoiceModal, openPaymentModal } = props;

  const { t } = useAppTranslation(['contracts', 'profile', 'common']);

  const currencySymbol = useGetCurrencySymbol();
  const { send } = useSendInvoiceByEmail();

  const columns = useMemo<ColumnsType<Invoice>>(
    () => [
      {
        title: t('Customer'),
        key: 'customer',
        render: (_, record) => <div className="text-primary font-normal">{`${record.user.firstName} ${record.user.lastName}`}</div>,
      },
      {
        title: t('Invoice'),
        key: 'invoice',
        render: (_, record) => (
          <div className="text-primary flex gap-1">
            <span
              className="text-accent underline cursor-pointer"
              onClick={() => {
                openInvoiceModal(record);
              }}
            >
              #{record.documentNumber}
            </span>
          </div>
        ),
      },
      {
        title: t('Unit type'),
        key: 'unitType',
        render: (_, record) => {
          const boxInvoiceItem = record.invoiceItems.find((invoiceItem) => invoiceItem.contractItem?.box);
          const box = boxInvoiceItem?.contractItem?.box;

          const depositInvoiceItem = record.invoiceItems.find(
            (invoiceItem) => invoiceItem.contractItem?.contractUnitType === ContractUnitType.DEPOSIT,
          );

          if (box) {
            return <div className="text-primary font-normal">{box.name}</div>;
          }

          if (!box && depositInvoiceItem) {
            return <div className="text-primary font-normal">{depositInvoiceItem.contractItem?.description}</div>;
          }

          if (!record.contract?.contractId) {
            return <div className="text-primary font-normal">{t('Personal Invoice', { ns: 'common' })}</div>;
          }

          if (record.relatedInvoiceId) {
            return <div className="text-primary font-normal">{t('Late charge')}</div>;
          }

          return <div className="text-primary font-normal">{t('Other', { ns: 'common' })}</div>;
        },
      },
      {
        title: t('Contract number'),
        key: 'contract',
        render: (_, record) => <div className="text-primary font-normal">#{record.contract?.contractNumber}</div>,
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',
        render: (_, record) => <div className="text-primary font-normal">{record.warehouse?.name}</div>,
      },
      {
        title: t('Total amount'),
        key: 'totalAmount',
        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              {formatPrice(record.amount)} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Due date'),
        key: 'dueDate',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.dueDate)}</div>,
      },
      {
        title: t('Created date'),
        key: 'createdDate',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
    ],
    [t, openInvoiceModal, currencySymbol],
  );

  const rowActions: Array<ServerTableRowActions<Invoice>> = useMemo(
    () => [
      {
        name: 'payInvoice',
        theme: 'clear',
        icon: <PaymentIcon className="w-5 h-5" />,
        description: t('Pay', { ns: 'common' }),
        label: t('Pay', { ns: 'common' }),
        onClick: openPaymentModal,
        isDisabled: (selectedInvoice) => selectedInvoice.debt === 0,
      },
      {
        name: 'downloadInvoice',
        theme: 'clear',
        icon: <DownloadIcon className="w-5 h-5" />,
        description: t('Download', { ns: 'common' }),
        label: t('Download', { ns: 'common' }),
        onClick: (selectedInvoice) => {
          const invoiceLink = generateInvoicePdfLink(selectedInvoice.invoiceId);

          window.open(invoiceLink);
        },
      },
      {
        name: 'sendEmail',
        theme: 'clear',
        icon: <SendEmailIcon className="w-5 h-5" />,
        description: t('Send by Email'),
        label: t('Send by Email'),
        onClick: (selectedInvoice) => {
          send(selectedInvoice.invoiceId);
        },
      },
    ],
    [openPaymentModal, send, t],
  );

  const highlightRow = (record: Invoice): string => {
    return classNames({
      'bg-successLight': record.debt === 0 && !record.creditNotes.length,
      'bg-warnLight': record.debt === 0 && record.creditNotes.length,
      'bg-gradient-to-r from-successLight to-warnLight': record.debt === 0 && record.creditNotes.length && record.payments.length,
      'bg-gradient-to-r from-successLight via-warnLight to-light': record.debt !== 0 && record.creditNotes.length && record.payments.length,
    });
  };

  return (
    <Modal width={1200} isOpen={isOpened} onClose={onCLose} className="z-50">
      <ServerTable
        columns={columns}
        fetchData={useGetUserInvoicesQuery}
        rowKey="invoiceId"
        rowClassName={highlightRow}
        defaultFilters={{ userId }}
        rowActions={rowActions}
        title={t('Personal invoices')}
        rowActionsLimit={0}
        pageSize={5}
      />
    </Modal>
  );
});
